// Weekly-dose programme planner (#92).
//
// The user gives limits: days a week, the most time a session can take, and
// optionally one long day. The goal's weekly targets (utils/weeklyTargets.js,
// the same numbers What's next and Activity measure against) set the dose:
// lifting days, hard interval work, cardio days (one of them VO2max) and Zone
// 2 minutes. The planner fits that dose into the days, gives each day a kind
// and a target length, and reports how much of the dose the plan covers.
// Days are single-type unless a mixed day helps: see weeklyMix.
//
// This is the only copy of the week-plan rules. The server stores the slots it
// is sent; api/lib/programmeSkeleton.js checks them and merges them into an
// existing programme.

import { computeWeeklyTargets } from "../utils/weeklyTargets";
import {
  getDeloadWeeks,
  resistanceDayAt,
  buildScheduledLabel,
  formatWeekList,
  FINISHER_SUFFIX,
} from "./periodisation";
import { weekdaysFor, WEEKDAY_NAMES } from "../utils/programmeSchedule";

// Weekly Zone 2 minutes by goal family. Galpin puts easy aerobic work at
// 60-100 min a week; endurance goals want more, and Attia's longevity
// protocol about 3 hours (docs/training_science.md §3). Tunable.
export const ZONE2_WEEKLY_MINUTES = {
  health: 90,
  muscle: 60,
  strength: 60,
  endurance: 150,
  performance: 60,
};

// Zone 2 does little below about 30 minutes continuous (§3, "30+ min").
export const ZONE2_MIN_MINUTES = 30;
// A sprint-interval day: warm-up, 5-6 min all-out in 20-90 s efforts, cool-down.
export const INTERVAL_MAX_MINUTES = 30;
// A VO2max day: warm-up, 4 x 4 min at 90-95% HRmax with 3 min easy between, cool-down.
export const VO2MAX_MAX_MINUTES = 40;
export const MIN_SESSION_MINUTES = 15;
// Deload weeks are shorter: about half the working sets, so about 70% of the time.
export const DELOAD_FACTOR = 0.7;
export const DEFAULT_SESSION_MINUTES = 45;

// Lifting day length relative to the limit. Heavy days need their long rests;
// moderate and power days are shorter by design.
const LENGTH_FACTOR = {
  "Heavy A": 1,
  Strength: 1,
  "Moderate B": 0.85,
  Hypertrophy: 0.85,
  Power: 0.75,
};

const round5 = (n) => Math.round(n / 5) * 5;
// The form allows up to 14 sessions a week (doubles).
const clampDays = (n) => Math.min(Math.max(Math.round(Number(n) || 3), 1), 14);
const positive = (n) => (Number(n) > 0 ? Number(n) : 0);

const isLifting = (kind) => kind === "resistance" || kind === "resistance_intervals";
const isZone2 = (kind) => kind === "zone2" || kind === "zone2_sprints";
const hasSprints = (kind) =>
  kind === "intervals" || kind === "zone2_sprints" || kind === "resistance_intervals";
const isCardio = (kind) => isZone2(kind) || kind === "vo2max";

// Lifting days that can take an interval finisher: moderate and hypertrophy
// days, and frameworks without day types (Block for muscle, Novice Linear).
// Never heavy, strength or power days, where fatigue would cost lift quality.
const takesFinisher = (dayType) =>
  dayType == null || dayType === "Moderate B" || dayType === "Hypertrophy";

/**
 * How many of each kind of day a week the goal asks for, fitted to the days
 * available.
 *
 * - The "cardio" target is VO2max work plus easy aerobic volume (Galpin;
 *   utils/trainingBalance.js), so with 2+ cardio sessions one is a VO2max day.
 * - Targets may add up to days + 1 (weeklyTargets.js). The extra hard-interval
 *   session then rides on another day (`extraIntervals`): as a short finisher
 *   after a moderate lifting day, which keeps easy days easy, or, for strength
 *   and power goals, as a sprint block on a Zone 2 day so the lifting stays
 *   fresh. Concurrent training costs little muscle or maximal strength, but
 *   same-session cardio can blunt power (Schumann 2022), and lifting goes
 *   first (Murlasits 2018). Beyond that, easy cardio gives way first.
 * - Spare days become easy aerobic work, the safest volume to add.
 */
export const weeklyMix = ({ focus, perWeek, userProfile } = {}) => {
  const days = clampDays(perWeek);
  const targets = computeWeeklyTargets({
    programme: { focus, days_per_week: days },
    userProfile,
  });
  const finisherAllowed = targets.family !== "strength";
  let resistance = Math.min(targets.counts.resistance, days);
  let intervals = targets.counts.anaerobic;
  let cardio = targets.counts.aerobic;
  let extraIntervals = false;
  const canCarry = () => cardio > 0 || (finisherAllowed && resistance > 0);
  let excess = resistance + intervals + cardio - days;
  if (excess > 0 && intervals > 0 && canCarry()) {
    intervals -= 1;
    extraIntervals = true;
    excess -= 1;
  }
  // A strength goal's sprints need a Zone 2 day to ride on, so keep one.
  const keepCardio = extraIntervals && !finisherAllowed ? 1 : 0;
  while (excess > 0 && cardio > keepCardio) {
    cardio -= 1;
    excess -= 1;
  }
  while (excess > 0 && intervals > 0) {
    intervals -= 1;
    excess -= 1;
  }
  if (excess > 0 && cardio > 0) {
    // Only reachable with overrides: no room left for any cardio day.
    cardio -= 1;
    excess -= 1;
  }
  if (extraIntervals && !canCarry()) extraIntervals = false;

  const vo2max = targets.counts.aerobic >= 2 && cardio >= 2 ? 1 : 0;
  let zone2 = cardio - vo2max;
  // Spare days: easy aerobic first; past a full week, alternate with lifting
  // so doubles don't become all cardio.
  for (let k = 0; k < -excess; k++) {
    if (k % 2 === 0 || days <= 7) zone2 += 1;
    else resistance += 1;
  }

  return {
    family: targets.family,
    days,
    targets: { ...targets.counts, vo2max: targets.counts.aerobic >= 2 ? 1 : 0 },
    resistance,
    intervals,
    vo2max,
    zone2,
    extraIntervals,
    finisherAllowed,
    zone2Target: targets.counts.aerobic > 0 ? ZONE2_WEEKLY_MINUTES[targets.family] : 0,
  };
};

// Circular gap in days between training days on a 7-day week.
const gapAfter = (days, i) => {
  const next = days[(i + 1) % days.length];
  return (next - days[i] + 7) % 7 || 7;
};

// All ways to choose `k` of `n` indices (n is at most 7).
const choose = (n, k, start = 0) => {
  if (k === 0) return [[]];
  const out = [];
  for (let i = start; i <= n - k; i++) {
    for (const rest of choose(n, k - 1, i + 1)) out.push([i, ...rest]);
  }
  return out;
};

/**
 * One week's day kinds for real training days (#92), 0 = Monday ... 6 =
 * Sunday. Lifting goes on the days with the most rest between them (the
 * choice whose shortest gap, wrapping into next week, is longest); hard cardio
 * goes as far from lifting as the days allow; Zone 2 takes the rest.
 */
export const weekPatternForDays = (mix, weekdays) => {
  const n = weekdays.length;
  const r = Math.min(mix.resistance, n);
  let lifting = [];
  if (r > 0) {
    let best = null;
    for (const pick of choose(n, r)) {
      const days = pick.map((i) => weekdays[i]);
      const gaps = days.map((_, i) => gapAfter(days, i));
      const score = [Math.min(...gaps), -Math.max(...gaps)];
      if (!best || score[0] > best.score[0] || (score[0] === best.score[0] && score[1] > best.score[1])) {
        best = { pick, score };
      }
    }
    lifting = best.pick;
  }
  const pattern = Array(n).fill(null);
  lifting.forEach((i) => (pattern[i] = "resistance"));
  const liftDays = lifting.map((i) => weekdays[i]);
  const distance = (i) =>
    liftDays.length ? Math.min(...liftDays.map((d) => Math.min((weekdays[i] - d + 7) % 7, (d - weekdays[i] + 7) % 7))) : 7;
  const free = pattern.map((k, i) => (k ? null : i)).filter((i) => i !== null);
  // Hard cardio first, onto the free days furthest from lifting.
  const byDistance = [...free].sort((a, b) => distance(b) - distance(a) || a - b);
  const hard = [...Array(mix.vo2max).fill("vo2max"), ...Array(mix.intervals).fill("intervals")];
  for (const kind of hard) {
    const i = byDistance.shift();
    if (i === undefined) break;
    pattern[i] = kind;
  }
  for (let i = 0; i < n; i++) if (!pattern[i]) pattern[i] = "zone2";
  return pattern;
};

/**
 * One week's day kinds in order, before any extra interval work is placed.
 * Lifting days are spread as evenly as the days allow; the gaps take easy and
 * hard cardio in turn, easy first, so hard days don't bunch together.
 */
export const weekPattern = (mix) => {
  const pattern = Array(mix.days).fill(null);
  for (let i = 0; i < mix.resistance; i++) {
    pattern[Math.round((i * mix.days) / mix.resistance)] = "resistance";
  }
  const easy = Array(mix.zone2).fill("zone2");
  const hard = [...Array(mix.vo2max).fill("vo2max"), ...Array(mix.intervals).fill("intervals")];
  const cardio = [];
  while (easy.length || hard.length) {
    if (easy.length) cardio.push(easy.shift());
    if (hard.length) cardio.push(hard.shift());
  }
  for (let d = 0; d < pattern.length; d++) {
    if (!pattern[d]) pattern[d] = cardio.shift() || "zone2";
  }
  return pattern;
};

const lastIndexWhere = (list, pred) => {
  for (let i = list.length - 1; i >= 0; i--) if (pred(list[i], i)) return i;
  return -1;
};

const CARDIO_NAMES = {
  zone2: "Zone 2",
  zone2_sprints: "Zone 2 + Sprints",
  vo2max: "VO2max",
  intervals: "Sprint Intervals",
};

/**
 * Every day of the programme, labelled, with its kind and target minutes.
 *
 * @param {object} o
 * @param {string} o.focus          programme goal
 * @param {number} o.durationWeeks
 * @param {number} o.perWeek        training days a week
 * @param {string} o.split          full_body | upper_lower | ppl | body_part
 * @param {string} o.framework      progression style for the lifting days
 * @param {number} o.sessionMinutes the most time a session can take
 * @param {number} [o.longMinutes]  one longer Zone 2 day a week, when set
 * @param {number[]} [o.weekdays]  training days, 0 = Monday (#92); spacing
 *                                  then uses the real gaps between them
 * @param {number[]} [o.deloadWeeks] deload weeks the user moved (#94); the
 *                                  default rule when absent
 * @param {object} [o.userProfile]  for accepted weekly-target overrides
 * @param {object[]} [o.edits]      changes from the notes review (#92), checked
 *                                  against the rules and applied after them;
 *                                  see applyPlanEdits
 * @returns {{ slots: object[], mix: object, coverage: object, deloads: number[],
 *             review: { accepted: object[], rejected: object[] } }}
 */
export const planProgramme = ({
  focus,
  durationWeeks,
  perWeek,
  split,
  framework,
  sessionMinutes,
  longMinutes,
  deloadWeeks,
  weekdays,
  userProfile,
  edits,
} = {}) => {
  const weeks = Math.max(1, Math.round(Number(durationWeeks) || 1));
  const limit = positive(sessionMinutes) || DEFAULT_SESSION_MINUTES;
  // A long day only means something when it is longer than the limit.
  const long = positive(longMinutes) > limit ? positive(longMinutes) : 0;
  const mix = weeklyMix({ focus, perWeek, userProfile });
  const days = Array.isArray(weekdays) && weekdays.length === mix.days ? weekdays : null;
  const pattern = days ? weekPatternForDays(mix, days) : weekPattern(mix);
  // The long day goes on a weekend Zone 2 day when there is one, else the
  // week's last Zone 2 day.
  const zone2Days = pattern.map((k, i) => (k === "zone2" ? i : -1)).filter((i) => i >= 0);
  const weekendZone2 = days ? zone2Days.filter((i) => days[i] >= 5) : [];
  const longIndex = !long
    ? -1
    : weekendZone2.length
      ? weekendZone2[weekendZone2.length - 1]
      : pattern.lastIndexOf("zone2");
  const review = { accepted: [], rejected: [] };
  const cleanEdits = (Array.isArray(edits) ? edits : []).map(normalisePlanEdit).filter(Boolean);
  const deloads = Array.isArray(deloadWeeks)
    ? storedDeloads(deloadWeeks.map(Number), cleanEdits, review)
    : moveDeloads(getDeloadWeeks(weeks), weeks, cleanEdits, review);

  const slots = [];
  const caps = []; // each slot's longest allowed length, for the edit checks
  let lifted = 0; // lifting days so far: the split and rotation count only these
  for (let w = 1; w <= weeks; w++) {
    const deload = deloads.includes(w);
    // This week's lifting days first: where the extra interval work goes
    // depends on which of them are moderate days this week.
    const kinds = [...pattern];
    const lifting = [];
    for (const k of kinds) {
      lifting.push(k === "resistance" ? resistanceDayAt(framework, split, lifted++) : null);
    }
    if (mix.extraIntervals) {
      const finisherDay = mix.finisherAllowed
        ? lastIndexWhere(lifting, (day) => day && takesFinisher(day.dayType))
        : -1;
      // Otherwise sprints on a Zone 2 day, preferring one that isn't the long day.
      const sprintDay =
        lastIndexWhere(kinds, (k, d) => k === "zone2" && d !== longIndex) >= 0
          ? lastIndexWhere(kinds, (k, d) => k === "zone2" && d !== longIndex)
          : kinds.lastIndexOf("zone2");
      if (finisherDay >= 0) kinds[finisherDay] = "resistance_intervals";
      else if (sprintDay >= 0) kinds[sprintDay] = "zone2_sprints";
    }

    for (let d = 0; d < kinds.length; d++) {
      const kind = kinds[d];
      let label;
      let minutes;
      let cap = limit;
      if (isLifting(kind)) {
        const { base, dayType } = lifting[d];
        label = dayType ? `${base} · ${dayType}` : base;
        // A finisher day carries extra work, so it gets the full limit.
        minutes = kind === "resistance_intervals" ? limit : limit * (LENGTH_FACTOR[dayType] ?? 1);
        if (kind === "resistance_intervals") label += FINISHER_SUFFIX;
      } else if (isZone2(kind)) {
        const isLong = d === longIndex;
        label = `${isLong ? "Long " : ""}${CARDIO_NAMES[kind]}`;
        minutes = isLong ? long : limit;
        if (isLong) cap = long;
      } else if (kind === "vo2max") {
        label = CARDIO_NAMES.vo2max;
        minutes = Math.min(limit, VO2MAX_MAX_MINUTES);
      } else {
        label = CARDIO_NAMES.intervals;
        minutes = Math.min(limit, INTERVAL_MAX_MINUTES);
      }
      if (deload) {
        label = `Deload · ${label}`;
        minutes *= DELOAD_FACTOR;
        cap = round5(cap * DELOAD_FACTOR);
      }
      caps.push(cap);
      slots.push({
        week_number: w,
        day_index: d,
        scheduled_label: label,
        day_kind: kind,
        target_minutes: Math.max(MIN_SESSION_MINUTES, Math.min(round5(minutes), cap)),
      });
    }
  }

  const edited = applyPlanEdits(slots, caps, {
    edits: cleanEdits,
    mix,
    weeks,
    deloads,
    review,
  });

  const firstNormal = edited.filter(
    (s) => s.week_number === (deloads.includes(1) ? 2 : 1),
  );
  return {
    slots: edited,
    mix,
    coverage: planCoverage(mix, firstNormal.length ? firstNormal : edited),
    deloads,
    review,
  };
};

// ─── Notes review edits (#92) ───────────────────────────────────────────────
//
// An LLM reads the programme notes and suggests changes in a fixed format; it
// never builds the plan. Every change is checked here against the same limits
// the rules use, and one that breaks them is left out. Accepted changes are
// stored on the programme (programmes.plan_edits) and re-applied whenever a
// week is re-planned, so a reset week keeps them. Each carries a plain-English
// reason that is saved on the days it touches (plan_note).

export const PLAN_EDIT_TYPES = ["set_kind", "set_minutes", "move_day", "move_deload", "add_note"];
// How far the review may move a deload, in weeks.
export const DELOAD_MOVE_MAX = 2;
export const PLAN_NOTE_MAX = 300;

const wholeNumber = (n) => {
  if (n == null || n === "") return null;
  const value = Number(n);
  return Number.isInteger(value) ? value : null;
};

/**
 * One edit in the stored shape, or null when it is not one:
 *   { type: "set_kind",    weeks: [n], day, kind, reason }
 *   { type: "set_minutes", weeks: [n], day, minutes, reason }
 *   { type: "move_day",    weeks: [n], day, to_day, reason }  swaps two days
 *   { type: "move_deload", from_week, to_week, reason }
 *   { type: "add_note",    weeks: [n], day, reason }
 * Days are 0-based positions in the week, as in the slots.
 */
export const normalisePlanEdit = (raw) => {
  if (!raw || !PLAN_EDIT_TYPES.includes(raw.type)) return null;
  const reason = String(raw.reason || "").trim().slice(0, PLAN_NOTE_MAX);
  if (!reason) return null;
  if (raw.type === "move_deload") {
    const from = wholeNumber(raw.from_week);
    const to = wholeNumber(raw.to_week);
    return from && to ? { type: raw.type, from_week: from, to_week: to, reason } : null;
  }
  const weeks = [...new Set((Array.isArray(raw.weeks) ? raw.weeks : [raw.weeks]).map(wholeNumber))]
    .filter((w) => w > 0)
    .sort((a, b) => a - b);
  const day = wholeNumber(raw.day);
  if (!weeks.length || day === null) return null;
  const edit = { type: raw.type, weeks, day };
  if (raw.type === "set_kind") {
    if (!DAY_KIND_LABELS[raw.kind]) return null;
    edit.kind = raw.kind;
  } else if (raw.type === "set_minutes") {
    edit.minutes = wholeNumber(raw.minutes);
    if (!edit.minutes) return null;
  } else if (raw.type === "move_day") {
    edit.to_day = wholeNumber(raw.to_day);
    if (edit.to_day === null) return null;
  }
  edit.reason = reason;
  return edit;
};

// Plain names for the kinds of day, for edit summaries and the review prompt.
export const DAY_KIND_LABELS = {
  resistance: "Strength",
  resistance_intervals: "Strength + Intervals",
  zone2: "Zone 2",
  zone2_sprints: "Zone 2 + Sprints",
  vo2max: "VO2max",
  intervals: "Sprint Intervals",
};

const reject = (review, edit, why) => review.rejected.push({ edit, why });

// The review's deload moves, on the default deload weeks.
const moveDeloads = (defaults, weeks, edits, review) => {
  const deloads = [...defaults];
  for (const edit of edits.filter((e) => e.type === "move_deload")) {
    const { from_week: from, to_week: to } = edit;
    if (!deloads.includes(from)) reject(review, edit, `Week ${from} isn't a deload week.`);
    else if (to < 1 || to > weeks) reject(review, edit, `Week ${to} is outside the programme.`);
    else if (to === 1) reject(review, edit, "The first week can't be a deload.");
    else if (deloads.includes(to)) reject(review, edit, `Week ${to} is already a deload week.`);
    else if (Math.abs(to - from) > DELOAD_MOVE_MAX) {
      reject(review, edit, `A deload moves at most ${DELOAD_MOVE_MAX} weeks.`);
    } else {
      deloads[deloads.indexOf(from)] = to;
      deloads.sort((a, b) => a - b);
      review.accepted.push(edit);
    }
  }
  return deloads;
};

// Deloads the user moved by hand (#94) are the record, and already include
// any move the review made before them. A review move they show stands; any
// other is left out rather than undoing the user's own move.
const storedDeloads = (deloads, edits, review) => {
  for (const edit of edits.filter((e) => e.type === "move_deload")) {
    if (deloads.includes(edit.to_week) && !deloads.includes(edit.from_week)) {
      review.accepted.push(edit);
    } else {
      reject(review, edit, "You've moved this programme's deload weeks yourself.");
    }
  }
  return deloads;
};

const kindCap = (kind) =>
  kind === "vo2max" ? VO2MAX_MAX_MINUTES : kind === "intervals" ? INTERVAL_MAX_MINUTES : Infinity;

const withNote = (slot, reason) => ({
  ...slot,
  plan_note: slot.plan_note ? `${slot.plan_note} ${reason}`.slice(0, 500) : reason,
});

// The label for a slot whose kind changes: keeps a Deload prefix, and a
// lifting day keeps its split and day type.
const relabel = (slot, kind) => {
  const deload = slot.scheduled_label.startsWith("Deload · ") ? "Deload · " : "";
  const base = slot.scheduled_label.replace(/^Deload · /, "");
  if (isLifting(kind)) {
    const lift = base.replace(FINISHER_SUFFIX, "");
    return deload + lift + (kind === "resistance_intervals" ? FINISHER_SUFFIX : "");
  }
  return deload + CARDIO_NAMES[kind];
};

// One edit on one week's slots (with their caps). Returns the new week or a
// reason it can't apply.
const editWeek = (week, edit) => {
  const at = week.find((x) => x.slot.day_index === edit.day);
  if (!at) return { why: `The week has no day ${edit.day + 1}.` };
  const { slot, cap } = at;
  const replace = (next) => week.map((x) => (x === at ? next : x));
  if (edit.type === "set_kind") {
    if (slot.day_kind === edit.kind) return { why: "The day is already that kind." };
    if (!isLifting(slot.day_kind) && isLifting(edit.kind)) {
      return { why: "Strength days come from the split rotation, so a cardio day can't become one." };
    }
    if (!slot.day_kind) return { why: "Days planned before day kinds can't change kind." };
    const limit = Math.min(cap, kindCap(edit.kind));
    return {
      week: replace({
        cap,
        slot: {
          ...slot,
          day_kind: edit.kind,
          scheduled_label: relabel(slot, edit.kind),
          target_minutes: Math.max(MIN_SESSION_MINUTES, Math.min(slot.target_minutes, limit)),
        },
      }),
    };
  }
  if (edit.type === "set_minutes") {
    const limit = Math.min(cap, kindCap(slot.day_kind));
    if (edit.minutes < MIN_SESSION_MINUTES) {
      return { why: `Sessions are at least ${MIN_SESSION_MINUTES} minutes.` };
    }
    if (edit.minutes > limit) return { why: `That day can take at most ${limit} minutes.` };
    return { week: replace({ cap, slot: { ...slot, target_minutes: edit.minutes } }) };
  }
  if (edit.type === "move_day") {
    const other = week.find((x) => x.slot.day_index === edit.to_day);
    if (!other || other === at) return { why: `The week has no other day ${edit.to_day + 1}.` };
    const swap = (x, from) => ({
      cap: from.cap,
      slot: { ...from.slot, week_number: x.slot.week_number, day_index: x.slot.day_index },
    });
    return {
      week: week.map((x) => (x === at ? swap(at, other) : x === other ? swap(other, at) : x)),
    };
  }
  return { week }; // add_note: only the note
};

// The days an edit touches in a week, after it applies.
const touchedDays = (edit) =>
  edit.type === "move_day" ? [edit.day, edit.to_day] : [edit.day];

/**
 * Apply the review's day edits to the rules' slots, one edit at a time. An
 * edit is all-or-nothing across its weeks, and is left out (with why) when it:
 * - names a week or day the plan doesn't have,
 * - makes a day shorter than the minimum or longer than its limit (the
 *   session limit, the long day's length, the VO2max or interval cap, and the
 *   shorter deload length in deload weeks), or
 * - leaves a normal week short of the dose the rules planned for it: fewer
 *   strength, VO2max, hard-interval or cardio days, or fewer Zone 2 minutes,
 *   than the rules gave it, up to the goal's weekly target.
 * Deload weeks are light by design, so only the limits apply there.
 */
const applyPlanEdits = (slots, caps, { edits, mix, weeks, deloads, review }) => {
  const byWeek = new Map();
  slots.forEach((slot, i) => {
    if (!byWeek.has(slot.week_number)) byWeek.set(slot.week_number, []);
    byWeek.get(slot.week_number).push({ slot, cap: caps[i] });
  });
  const baseline = new Map(
    [...byWeek].map(([w, list]) => [w, planCoverage(mix, list.map((x) => x.slot))]),
  );
  const short = (week, list) => {
    const before = baseline.get(week);
    const after = planCoverage(mix, list.map((x) => x.slot));
    const tags = ["resistance", "vo2max", "anaerobic", "aerobic"];
    const lost = tags.find(
      (t) => after.planned[t] < Math.min(before.planned[t], mix.targets[t] || 0),
    );
    if (lost) return true;
    return after.zone2Minutes < Math.min(before.zone2Minutes, mix.zone2Target || 0);
  };

  for (const edit of edits.filter((e) => e.type !== "move_deload")) {
    const outside = edit.weeks.find((w) => w > weeks);
    if (outside) {
      reject(review, edit, `Week ${outside} is outside the programme.`);
      continue;
    }
    const next = new Map();
    let why = null;
    for (const w of edit.weeks) {
      const result = editWeek(byWeek.get(w), edit);
      if (result.why) {
        why = `Week ${w}: ${result.why}`;
        break;
      }
      if (!deloads.includes(w) && short(w, result.week)) {
        why = `Week ${w} would fall short of its weekly targets.`;
        break;
      }
      const days = touchedDays(edit);
      next.set(
        w,
        result.week.map((x) =>
          days.includes(x.slot.day_index) ? { ...x, slot: withNote(x.slot, edit.reason) } : x,
        ),
      );
    }
    if (why) {
      reject(review, edit, why);
      continue;
    }
    for (const [w, list] of next) byWeek.set(w, list);
    review.accepted.push(edit);
  }

  // A moved deload's reason goes on the days of the week it moved to.
  for (const edit of review.accepted.filter((e) => e.type === "move_deload")) {
    const list = byWeek.get(edit.to_week) || [];
    byWeek.set(edit.to_week, list.map((x) => ({ ...x, slot: withNote(x.slot, edit.reason) })));
  }

  return [...byWeek.values()]
    .flat()
    .map((x) => x.slot)
    .sort((a, b) => a.week_number - b.week_number || a.day_index - b.day_index);
};

/** A short plain-English summary of one edit: "Week 10, day 3: 30 min". */
export const describePlanEdit = (edit, weekdays) => {
  if (edit.type === "move_deload") {
    return `Deload moves from week ${edit.from_week} to week ${edit.to_week}`;
  }
  const dayName = (d) =>
    weekdays && weekdays[d] != null ? WEEKDAY_NAMES[weekdays[d]] : `day ${d + 1}`;
  const weeks =
    edit.weeks.length === 1 ? `Week ${edit.weeks[0]}` : `Weeks ${formatWeekList(edit.weeks)}`;
  const where = `${weeks}, ${dayName(edit.day)}`;
  if (edit.type === "set_kind") return `${where} becomes ${DAY_KIND_LABELS[edit.kind]}`;
  if (edit.type === "set_minutes") return `${where} is ${edit.minutes} min`;
  if (edit.type === "move_day") return `${where} swaps with ${dayName(edit.to_day)}`;
  return `${where}: note`;
};

/**
 * How much of the weekly dose one normal week covers: lifting, VO2max,
 * hard-interval and cardio days against the goal's targets, and Zone 2
 * minutes against the minutes target.
 */
export const planCoverage = (mix, weekSlots) => {
  const count = (pred) => weekSlots.filter((s) => pred(s.day_kind)).length;
  const zone2Slots = weekSlots.filter((s) => isZone2(s.day_kind));
  return {
    targets: mix.targets,
    planned: {
      resistance: count(isLifting),
      vo2max: count((k) => k === "vo2max"),
      anaerobic: count(hasSprints),
      aerobic: count(isCardio),
    },
    zone2Minutes: zone2Slots.reduce((sum, s) => sum + s.target_minutes, 0),
    zone2Target: mix.zone2Target,
    shortZone2: zone2Slots.some((s) => s.target_minutes < ZONE2_MIN_MINUTES),
  };
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * The coverage line under the plan preview, in plain English: "Each week:
 * 2 strength sessions, 1 VO2max session, 1 hard interval session and 60 of 90
 * Zone 2 minutes."
 */
export const describeCoverage = (coverage) => {
  if (!coverage) return "";
  const { planned, targets } = coverage;
  const part = (tag, one, many) => {
    const done = planned[tag] || 0;
    const want = targets[tag] || 0;
    if (!done && !want) return null;
    if (done >= want) return plural(done, one, many);
    return `${done} of ${want} ${want === 1 ? one : many}`;
  };
  const hasZone2 = coverage.zone2Minutes > 0;
  const parts = [
    part("resistance", "strength session", "strength sessions"),
    part("vo2max", "VO2max session", "VO2max sessions"),
    part("anaerobic", "hard interval session", "hard interval sessions"),
    coverage.zone2Target > 0 && hasZone2
      ? coverage.zone2Minutes >= coverage.zone2Target
        ? `${coverage.zone2Minutes} Zone 2 minutes`
        : `${coverage.zone2Minutes} of ${coverage.zone2Target} Zone 2 minutes`
      : hasZone2 || targets.aerobic
        ? part("aerobic", "cardio session", "cardio sessions")
        : null,
  ].filter(Boolean);
  const list =
    parts.length > 1
      ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
      : parts[0] || "";
  return list ? `Each week: ${list}.` : "";
};

/** What would close a gap, when there is one. Null when the plan covers the dose. */
export const coverageAdvice = (coverage) => {
  if (!coverage) return null;
  const { planned, targets } = coverage;
  if (planned.aerobic === 0 && targets.aerobic > 0) {
    return "Another day a week would fit your cardio target.";
  }
  if (coverage.shortZone2) {
    return `Zone 2 works best at ${ZONE2_MIN_MINUTES} minutes or more, so these sessions are on the short side.`;
  }
  if (coverage.zone2Target > 0 && coverage.zone2Minutes < coverage.zone2Target) {
    return "A long day, more time per session or another day a week would close the Zone 2 gap.";
  }
  if (
    planned.resistance < targets.resistance ||
    planned.anaerobic < targets.anaerobic ||
    planned.vo2max < (targets.vo2max || 0)
  ) {
    return "Another day a week would fit the rest of your weekly targets.";
  }
  return null;
};

/** Planner inputs from a stored programme row. */
export const planInputsFromProgramme = (programme, userProfile) => ({
  focus: programme?.focus,
  durationWeeks: programme?.duration_weeks,
  perWeek: programme?.days_per_week,
  split: programme?.split,
  framework: programme?.progression_style,
  sessionMinutes: programme?.session_minutes,
  longMinutes: programme?.long_minutes,
  deloadWeeks: Array.isArray(programme?.deload_weeks) ? programme.deload_weeks : undefined,
  weekdays: weekdaysFor(programme),
  edits: Array.isArray(programme?.plan_edits) ? programme.plan_edits : undefined,
  userProfile,
});

/** Whether a programme was built before day kinds existed (all lifting days). */
export const isLegacyProgramme = (programme) =>
  !(programme?.weeks || []).some((w) => (w.sessions || []).some((s) => s.day_kind));

/**
 * One week's planned slots, for "reset week". A programme built before day
 * kinds keeps its all-lifting labels so the reset week matches the rest of it.
 */
export const planWeek = (programme, weekNumber, userProfile) => {
  const week = Number(weekNumber);
  if (isLegacyProgramme(programme)) {
    return Array.from({ length: Number(programme.days_per_week) || 0 }, (_, d) => ({
      week_number: week,
      day_index: d,
      scheduled_label: buildScheduledLabel({
        framework: programme.progression_style,
        split: programme.split,
        perWeek: programme.days_per_week,
        weekNumber: week,
        dayIndex: d,
        durationWeeks: programme.duration_weeks,
        deloadWeeks: Array.isArray(programme.deload_weeks) ? programme.deload_weeks : undefined,
      }),
      day_kind: null,
      target_minutes: null,
    }));
  }
  return planProgramme(planInputsFromProgramme(programme, userProfile)).slots.filter(
    (s) => s.week_number === week,
  );
};

/**
 * Shortest and longest planned session outside deload weeks, for the header:
 * { min: 30, max: 45 }. Falls back to the programme-wide length.
 */
export const programmeMinutesRange = (programme) => {
  const minutes = (programme?.weeks || [])
    .flatMap((w) => w.sessions || [])
    .filter((s) => !String(s.scheduled_label || "").startsWith("Deload"))
    .map((s) => Number(s.target_minutes))
    .filter((n) => n > 0);
  if (!minutes.length) {
    const flat = positive(programme?.session_minutes);
    return flat ? { min: flat, max: flat } : null;
  }
  return { min: Math.min(...minutes), max: Math.max(...minutes) };
};

/** "45 min" or "30–45 min". */
export const formatMinutesRange = (range) => {
  if (!range) return "";
  return range.min === range.max
    ? `${range.max} min`
    : `${range.min}–${range.max} min`;
};
